import React, { useState, useEffect } from 'react';
import { transferAPI } from '../services/api';
import SearchableDropdown from './SearchableDropdown';

const TransferManagement = () => {
  // State management
  const [transfers, setTransfers] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [statuses, setStatuses] = useState({});
  const [types, setTypes] = useState({});
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    min_amount: '',
    max_amount: '',
    from_date: '',
    to_date: '',
    per_page: 20,
    page: 1
  });

  // Debounced search
  const [searchInput, setSearchInput] = useState('');

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Handle search debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput, page: 1 }));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Load data when filters change
  useEffect(() => {
    loadTransfers();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [statsRes, statusesRes, typesRes, usersRes, accountsRes] = await Promise.all([
        transferAPI.getStatistics(),
        transferAPI.getStatuses(),
        transferAPI.getTypes(),
        transferAPI.getUsers(),
        transferAPI.getAccounts()
      ]);
      
      setStatistics(statsRes.data);
      setStatuses(statusesRes.data);
      setTypes(typesRes.data);
      setUsers(usersRes.data);
      setAccounts(accountsRes.data);
    } catch (err) {
      setError('Failed to load initial data');
      console.error('Initial data loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadTransfers = async () => {
    try {
      setLoading(true);
      const response = await transferAPI.getTransfers(filters);
      setTransfers(response.data);
    } catch (err) {
      setError('Failed to load transfers');
      console.error('Transfers loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({
      search: '',
      status: '',
      type: '',
      min_amount: '',
      max_amount: '',
      from_date: '',
      to_date: '',
      per_page: 20,
      page: 1
    });
  };

  const handleViewItem = async (itemId) => {
    try {
      const response = await transferAPI.getTransfer(itemId);
      setSelectedItem(response.data);
      setShowViewModal(true);
    } catch (err) {
      setError('Failed to load transfer details');
    }
  };

  const handleEditItem = async (itemId) => {
    try {
      const response = await transferAPI.getTransfer(itemId);
      setSelectedItem(response.data);
      setShowEditModal(true);
    } catch (err) {
      setError('Failed to load transfer details');
    }
  };

  const handleDeleteItem = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleCreateTransfer = () => {
    setShowCreateModal(true);
  };

  const handleCancelTransfer = async (itemId) => {
    try {
      await transferAPI.cancelTransfer(itemId);
      await loadTransfers();
      await loadInitialData();
      setError(null);
    } catch (err) {
      setError('Failed to cancel transfer: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleProcessTransfer = async (itemId) => {
    try {
      await transferAPI.processTransfer(itemId);
      await loadTransfers();
      await loadInitialData();
      setError(null);
    } catch (err) {
      setError('Failed to process transfer: ' + (err.response?.data?.message || err.message));
    }
  };

  const executeDeleteItem = async () => {
    try {
      await transferAPI.deleteTransfer(selectedItem.id);
      await loadTransfers();
      await loadInitialData();
      setShowDeleteModal(false);
      setSelectedItem(null);
      setError(null);
    } catch (err) {
      setError('Failed to delete transfer: ' + (err.response?.data?.message || err.message));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    
    // Handle both string and object responses from API
    let statusLabel = status;
    if (statuses[status]) {
      statusLabel = typeof statuses[status] === 'object' ? statuses[status].name || statuses[status].description || status : statuses[status];
    }
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabel}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      internal: 'bg-blue-100 text-blue-800',
      external: 'bg-purple-100 text-purple-800',
      wire: 'bg-green-100 text-green-800',
      ach: 'bg-orange-100 text-orange-800',
      sepa: 'bg-indigo-100 text-indigo-800'
    };
    
    // Handle both string and object responses from API
    let typeLabel = type;
    if (types[type]) {
      typeLabel = typeof types[type] === 'object' ? types[type].name || types[type].description || type : types[type];
    }
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeColors[type] || 'bg-gray-100 text-gray-800'}`}>
        {typeLabel}
      </span>
    );
  };

  // Helper function to get account type display name
  const getAccountTypeDisplay = (accountType) => {
    if (typeof accountType === 'string') {
      return accountType;
    }
    if (typeof accountType === 'object' && accountType) {
      return accountType.name || accountType.description || accountType.code || 'Unknown Type';
    }
    return 'Unknown Type';
  };

  if (loading && !transfers.data) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Transfer Management</h1>
          <p className="mt-2 text-sm text-gray-600">
            Monitor and manage all transfer transactions
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
            <span 
              className="absolute top-0 bottom-0 right-0 px-4 py-3 cursor-pointer"
              onClick={() => setError(null)}
            >
              <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <title>Close</title>
                <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
              </svg>
            </span>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-sm font-medium">🔄</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Transfers</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statistics.total_transfers || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-sm font-medium">✅</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statistics.completed_transfers || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-sm font-medium">⏳</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {statistics.pending_transfers || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                    <span className="text-white text-sm font-medium">💰</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Monthly Volume</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {formatCurrency(statistics.total_volume_month || 0)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Search</label>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Transfer ID, description, user..."
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">All Statuses</option>
                  {Array.isArray(statuses) ? 
                    statuses.map((status) => (
                      <option key={status.id || status.code} value={status.code || status.id}>
                        {status.name || status.description || status.code || status.id}
                      </option>
                    )) :
                    Object.entries(statuses).map(([key, value]) => (
                      <option key={key} value={key}>
                        {typeof value === 'object' ? value.name || value.description || key : value}
                      </option>
                    ))
                  }
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value="">All Types</option>
                  {Array.isArray(types) ? 
                    types.map((type) => (
                      <option key={type.id || type.code} value={type.code || type.id}>
                        {type.name || type.description || type.code || type.id}
                      </option>
                    )) :
                    Object.entries(types).map(([key, value]) => (
                      <option key={key} value={key}>
                        {typeof value === 'object' ? value.name || value.description || key : value}
                      </option>
                    ))
                  }
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <TransfersTable 
          transfers={transfers}
          loading={loading}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          getStatusBadge={getStatusBadge}
          getTypeBadge={getTypeBadge}
          onView={handleViewItem}
          onEdit={handleEditItem}
          onDelete={handleDeleteItem}
          onCreate={handleCreateTransfer}
          onCancel={handleCancelTransfer}
          onProcess={handleProcessTransfer}
        />

        {/* Modals */}
        {showViewModal && selectedItem && (
          <ViewModal 
            transfer={selectedItem}
            onClose={() => setShowViewModal(false)}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            getStatusBadge={getStatusBadge}
            getTypeBadge={getTypeBadge}
            getAccountTypeDisplay={getAccountTypeDisplay}
          />
        )}

        {showEditModal && selectedItem && (
          <EditModal 
            transfer={selectedItem}
            onClose={() => {setShowEditModal(false); setSelectedItem(null);}}
            onSave={async (data) => {
              try {
                await transferAPI.updateTransfer(selectedItem.id, data);
                await loadTransfers();
                await loadInitialData();
                setShowEditModal(false);
                setSelectedItem(null);
                setError(null);
              } catch (err) {
                setError('Failed to update transfer: ' + (err.response?.data?.message || err.message));
              }
            }}
            users={users}
            accounts={accounts}
            statuses={statuses}
            types={types}
            error={error}
            getAccountTypeDisplay={getAccountTypeDisplay}
          />
        )}

        {showCreateModal && (
          <CreateModal 
            onClose={() => setShowCreateModal(false)}
            onSave={async (data) => {
              try {
                await transferAPI.createTransfer(data);
                await loadTransfers();
                await loadInitialData();
                setShowCreateModal(false);
                setError(null);
              } catch (err) {
                setError('Failed to create transfer: ' + (err.response?.data?.message || err.message));
              }
            }}
            users={users}
            accounts={accounts}
            statuses={statuses}
            types={types}
            error={error}
            getAccountTypeDisplay={getAccountTypeDisplay}
          />
        )}

        {showDeleteModal && selectedItem && (
          <DeleteModal 
            transfer={selectedItem}
            onClose={() => {setShowDeleteModal(false); setSelectedItem(null);}}
            onConfirm={executeDeleteItem}
            formatCurrency={formatCurrency}
          />
        )}
      </div>
    </div>
  );
};

// Transfers Table Component
const TransfersTable = ({ transfers, loading, formatCurrency, formatDate, getStatusBadge, getTypeBadge, onView, onEdit, onDelete, onCreate, onCancel, onProcess }) => (
  <div className="bg-white shadow overflow-hidden sm:rounded-md">
    <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
      <h3 className="text-lg font-medium text-gray-900">
        Transfers
        {transfers.total && (
          <span className="ml-2 text-sm text-gray-500">
            ({transfers.total} total)
          </span>
        )}
      </h3>
      <button
        onClick={onCreate}
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
      >
        Create New Transfer
      </button>
    </div>
    
    {loading ? (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    ) : transfers.data && transfers.data.length > 0 ? (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Transfer ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {transfers.data.map((transfer) => (
              <tr key={transfer.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {transfer.transfer_id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div>
                    <div className="font-medium">{transfer.user.name}</div>
                    <div className="text-gray-400">{transfer.user.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getTypeBadge(transfer.type)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {formatCurrency(transfer.amount)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(transfer.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(transfer.created_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onView(transfer.id)}
                      className="text-blue-600 hover:text-blue-900"
                      title="View Details"
                    >
                      View
                    </button>
                    <button
                      onClick={() => onEdit(transfer.id)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="Edit Transfer"
                    >
                      Edit
                    </button>
                    {transfer.status === 'pending' && (
                      <>
                        <button
                          onClick={() => onProcess(transfer.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Process Transfer"
                        >
                          Process
                        </button>
                        <button
                          onClick={() => onCancel(transfer.id)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Cancel Transfer"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => onDelete(transfer)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Transfer"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <div className="text-center py-12">
        <div className="text-gray-500">
          <div className="text-4xl mb-4">🔄</div>
          <h3 className="text-lg font-medium">No transfers found</h3>
          <p className="text-sm">Try adjusting your filters to see more results.</p>
        </div>
      </div>
    )}
  </div>
);

// View Modal Component
const ViewModal = ({ transfer, onClose, formatCurrency, formatDate, getStatusBadge, getTypeBadge, getAccountTypeDisplay }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Transfer Details</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Transfer ID</label>
            <p className="mt-1 text-sm text-gray-900 font-mono">{transfer.transfer_id}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <div className="mt-1">{getStatusBadge(transfer.status)}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Type</label>
            <div className="mt-1">{getTypeBadge(transfer.type)}</div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Amount</label>
            <p className="mt-1 text-sm text-gray-900 font-semibold">{formatCurrency(transfer.amount)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Fee</label>
            <p className="mt-1 text-sm text-gray-900">{formatCurrency(transfer.fee)}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">User</label>
            <p className="mt-1 text-sm text-gray-900">{transfer.user?.name} ({transfer.user?.email})</p>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <p className="mt-1 text-sm text-gray-900">{transfer.description || 'No description'}</p>
        </div>

        {transfer.from_account && (
          <div>
            <label className="block text-sm font-medium text-gray-700">From Account</label>
            <p className="mt-1 text-sm text-gray-900">
              {transfer.from_account.account_number} - {getAccountTypeDisplay(transfer.from_account.account_type)}
            </p>
          </div>
        )}

        {transfer.to_account && (
          <div>
            <label className="block text-sm font-medium text-gray-700">To Account</label>
            <p className="mt-1 text-sm text-gray-900">
              {transfer.to_account.account_number} - {getAccountTypeDisplay(transfer.to_account.account_type)}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Created Date</label>
            <p className="mt-1 text-sm text-gray-900">{formatDate(transfer.created_at)}</p>
          </div>
          {transfer.completed_date && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Completed Date</label>
              <p className="mt-1 text-sm text-gray-900">{formatDate(transfer.completed_date)}</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

// Edit Modal Component
const EditModal = ({ transfer, onClose, onSave, users, accounts, statuses, types, error, getAccountTypeDisplay }) => {
  const [formData, setFormData] = React.useState({
    user_id: transfer.user?.id || '',
    from_account_id: transfer.from_account?.id || '',
    to_account_id: transfer.to_account?.id || '',
    transfer_id: transfer.transfer_id || '',
    amount: transfer.amount || '',
    fee: transfer.fee || '',
    type: transfer.type || '',
    status: transfer.status || '',
    description: transfer.description || '',
    scheduled_date: transfer.scheduled_date ? transfer.scheduled_date.split('T')[0] : '',
    completed_date: transfer.completed_date ? transfer.completed_date.split('T')[0] : '',
  });
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Edit Transfer</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Transfer ID</label>
              <input
                type="text"
                name="transfer_id"
                value={formData.transfer_id}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">User</label>
              <SearchableDropdown
                options={users.map(user => ({
                  value: user.id,
                  label: `${user.name} (${user.email})`,
                  user: user
                }))}
                value={formData.user_id}
                onChange={(value) => handleChange({ target: { name: 'user_id', value } })}
                placeholder="Search and select user..."
                searchPlaceholder="Search by name or email..."
                getSearchText={(option) => {
                  const user = option.user || {};
                  const searchFields = [
                    user.name,
                    user.email,
                    user.account_number,
                    user.phone
                  ].filter(Boolean);
                  return searchFields.join(' ');
                }}
                renderOption={(option) => (
                  <div>
                    <p className="font-medium text-gray-900">{option.user.name}</p>
                    <p className="text-sm text-gray-500">{option.user.email}</p>
                    {option.user.account_number && (
                      <p className="text-xs text-gray-400">Account: {option.user.account_number}</p>
                    )}
                  </div>
                )}
                renderSelected={(option) => (
                  <div>
                    <span className="font-medium">{option.user.name}</span>
                    <span className="text-gray-500 ml-2">({option.user.email})</span>
                  </div>
                )}
                className="mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">From Account</label>
              <SearchableDropdown
                options={accounts.map(account => ({
                  value: account.id,
                  label: `${account.account_number} - ${getAccountTypeDisplay(account.account_type)} (${account.user?.name})`,
                  account: account
                }))}
                value={formData.from_account_id}
                onChange={(value) => handleChange({ target: { name: 'from_account_id', value } })}
                placeholder="Search and select from account..."
                searchPlaceholder="Search by account number, type, or user..."
                getSearchText={(option) => {
                  const account = option.account || {};
                  const user = account.user || {};
                  const searchFields = [
                    account.account_number,
                    getAccountTypeDisplay(account.account_type),
                    user.name,
                    user.email,
                    user.phone,
                    account.balance?.toString()
                  ].filter(Boolean);
                  return searchFields.join(' ');
                }}
                renderOption={(option) => (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">****{option.account.account_number?.slice(-4) || '0000'}</p>
                      <p className="text-sm text-gray-500">{getAccountTypeDisplay(option.account.account_type)}</p>
                      <p className="text-xs text-gray-400">{option.account.user?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        ${option.account.balance?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                )}
                renderSelected={(option) => (
                  <div>
                    <span className="font-medium">****{option.account.account_number?.slice(-4) || '0000'}</span>
                    <span className="text-gray-500 ml-2">- {getAccountTypeDisplay(option.account.account_type)}</span>
                  </div>
                )}
                className="mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">To Account</label>
              <SearchableDropdown
                options={accounts
                  .filter(account => account.id !== formData.from_account_id)
                  .map(account => ({
                    value: account.id,
                    label: `${account.account_number} - ${getAccountTypeDisplay(account.account_type)} (${account.user?.name})`,
                    account: account
                  }))}
                value={formData.to_account_id}
                onChange={(value) => handleChange({ target: { name: 'to_account_id', value } })}
                placeholder="Search and select to account (optional)..."
                searchPlaceholder="Search by account number, type, or user..."
                getSearchText={(option) => {
                  const account = option.account || {};
                  const user = account.user || {};
                  const searchFields = [
                    account.account_number,
                    getAccountTypeDisplay(account.account_type),
                    user.name,
                    user.email,
                    user.phone,
                    account.balance?.toString()
                  ].filter(Boolean);
                  return searchFields.join(' ');
                }}
                renderOption={(option) => (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">****{option.account.account_number?.slice(-4) || '0000'}</p>
                      <p className="text-sm text-gray-500">{getAccountTypeDisplay(option.account.account_type)}</p>
                      <p className="text-xs text-gray-400">{option.account.user?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        ${option.account.balance?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                )}
                renderSelected={(option) => (
                  <div>
                    <span className="font-medium">****{option.account.account_number?.slice(-4) || '0000'}</span>
                    <span className="text-gray-500 ml-2">- {getAccountTypeDisplay(option.account.account_type)}</span>
                  </div>
                )}
                className="mt-1"
                emptyMessage="No available accounts (select a different source account)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Amount</label>
              <input
                type="number"
                step="0.01"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Fee</label>
              <input
                type="number"
                step="0.01"
                name="fee"
                value={formData.fee}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              >
                <option value="">Select Type</option>
                {Array.isArray(types) ? 
                  types.map((type) => (
                    <option key={type.id || type.code} value={type.code || type.id}>
                      {type.name || type.description || type.code || type.id}
                    </option>
                  )) :
                  Object.entries(types).map(([key, value]) => (
                    <option key={key} value={key}>
                      {typeof value === 'object' ? value.name || value.description || key : value}
                    </option>
                  ))
                }
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              >
                <option value="">Select Status</option>
                {Array.isArray(statuses) ? 
                  statuses.map((status) => (
                    <option key={status.id || status.code} value={status.code || status.id}>
                      {status.name || status.description || status.code || status.id}
                    </option>
                  )) :
                  Object.entries(statuses).map(([key, value]) => (
                    <option key={key} value={key}>
                      {typeof value === 'object' ? value.name || value.description || key : value}
                    </option>
                  ))
                }
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Scheduled Date</label>
              <input
                type="date"
                name="scheduled_date"
                value={formData.scheduled_date}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Completed Date</label>
              <input
                type="date"
                name="completed_date"
                value={formData.completed_date}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Modal Component
const CreateModal = ({ onClose, onSave, users, accounts, statuses, types, error, getAccountTypeDisplay }) => {
  const [formData, setFormData] = React.useState({
    user_id: '',
    from_account_id: '',
    to_account_id: '',
    transfer_id: '',
    amount: '',
    fee: '0',
    type: '',
    status: 'pending',
    description: '',
    scheduled_date: '',
    completed_date: '',
  });
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Create New Transfer</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Transfer ID</label>
              <input
                type="text"
                name="transfer_id"
                value={formData.transfer_id}
                onChange={handleChange}
                placeholder="Leave empty to auto-generate"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">User *</label>
              <SearchableDropdown
                options={users.map(user => ({
                  value: user.id,
                  label: `${user.name} (${user.email})`,
                  user: user
                }))}
                value={formData.user_id}
                onChange={(value) => handleChange({ target: { name: 'user_id', value } })}
                placeholder="Search and select user..."
                searchPlaceholder="Search by name or email..."
                getSearchText={(option) => {
                  const user = option.user || {};
                  const searchFields = [
                    user.name,
                    user.email,
                    user.account_number,
                    user.phone
                  ].filter(Boolean);
                  return searchFields.join(' ');
                }}
                renderOption={(option) => (
                  <div>
                    <p className="font-medium text-gray-900">{option.user.name}</p>
                    <p className="text-sm text-gray-500">{option.user.email}</p>
                    {option.user.account_number && (
                      <p className="text-xs text-gray-400">Account: {option.user.account_number}</p>
                    )}
                  </div>
                )}
                renderSelected={(option) => (
                  <div>
                    <span className="font-medium">{option.user.name}</span>
                    <span className="text-gray-500 ml-2">({option.user.email})</span>
                  </div>
                )}
                className="mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">From Account *</label>
              <SearchableDropdown
                options={accounts.map(account => ({
                  value: account.id,
                  label: `${account.account_number} - ${getAccountTypeDisplay(account.account_type)} (${account.user?.name})`,
                  account: account
                }))}
                value={formData.from_account_id}
                onChange={(value) => handleChange({ target: { name: 'from_account_id', value } })}
                placeholder="Search and select from account..."
                searchPlaceholder="Search by account number, type, or user..."
                getSearchText={(option) => {
                  const account = option.account || {};
                  const user = account.user || {};
                  const searchFields = [
                    account.account_number,
                    getAccountTypeDisplay(account.account_type),
                    user.name,
                    user.email,
                    user.phone,
                    account.balance?.toString()
                  ].filter(Boolean);
                  return searchFields.join(' ');
                }}
                renderOption={(option) => (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">****{option.account.account_number?.slice(-4) || '0000'}</p>
                      <p className="text-sm text-gray-500">{getAccountTypeDisplay(option.account.account_type)}</p>
                      <p className="text-xs text-gray-400">{option.account.user?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        ${option.account.balance?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                )}
                renderSelected={(option) => (
                  <div>
                    <span className="font-medium">****{option.account.account_number?.slice(-4) || '0000'}</span>
                    <span className="text-gray-500 ml-2">- {getAccountTypeDisplay(option.account.account_type)}</span>
                  </div>
                )}
                className="mt-1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">To Account</label>
              <SearchableDropdown
                options={accounts
                  .filter(account => account.id !== formData.from_account_id)
                  .map(account => ({
                    value: account.id,
                    label: `${account.account_number} - ${getAccountTypeDisplay(account.account_type)} (${account.user?.name})`,
                    account: account
                  }))}
                value={formData.to_account_id}
                onChange={(value) => handleChange({ target: { name: 'to_account_id', value } })}
                placeholder="Search and select to account (optional for external transfers)..."
                searchPlaceholder="Search by account number, type, or user..."
                getSearchText={(option) => {
                  const account = option.account || {};
                  const user = account.user || {};
                  const searchFields = [
                    account.account_number,
                    getAccountTypeDisplay(account.account_type),
                    user.name,
                    user.email,
                    user.phone,
                    account.balance?.toString()
                  ].filter(Boolean);
                  return searchFields.join(' ');
                }}
                renderOption={(option) => (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">****{option.account.account_number?.slice(-4) || '0000'}</p>
                      <p className="text-sm text-gray-500">{getAccountTypeDisplay(option.account.account_type)}</p>
                      <p className="text-xs text-gray-400">{option.account.user?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        ${option.account.balance?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                )}
                renderSelected={(option) => (
                  <div>
                    <span className="font-medium">****{option.account.account_number?.slice(-4) || '0000'}</span>
                    <span className="text-gray-500 ml-2">- {getAccountTypeDisplay(option.account.account_type)}</span>
                  </div>
                )}
                className="mt-1"
                emptyMessage="No available accounts (select a different source account)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Amount *</label>
              <input
                type="number"
                step="0.01"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Fee</label>
              <input
                type="number"
                step="0.01"
                name="fee"
                value={formData.fee}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Type *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              >
                <option value="">Select Type</option>
                {Array.isArray(types) ? 
                  types.map((type) => (
                    <option key={type.id || type.code} value={type.code || type.id}>
                      {type.name || type.description || type.code || type.id}
                    </option>
                  )) :
                  Object.entries(types).map(([key, value]) => (
                    <option key={key} value={key}>
                      {typeof value === 'object' ? value.name || value.description || key : value}
                    </option>
                  ))
                }
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                required
              >
                {Array.isArray(statuses) ? 
                  statuses.map((status) => (
                    <option key={status.id || status.code} value={status.code || status.id}>
                      {status.name || status.description || status.code || status.id}
                    </option>
                  )) :
                  Object.entries(statuses).map(([key, value]) => (
                    <option key={key} value={key}>
                      {typeof value === 'object' ? value.name || value.description || key : value}
                    </option>
                  ))
                }
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Scheduled Date</label>
              <input
                type="date"
                name="scheduled_date"
                value={formData.scheduled_date}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Completed Date</label>
              <input
                type="date"
                name="completed_date"
                value={formData.completed_date}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Optional description for the transfer"
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Transfer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Modal Component
const DeleteModal = ({ transfer, onClose, onConfirm, formatCurrency }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
      <div className="mt-3 text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
          <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mt-2">Delete Transfer</h3>
        <div className="mt-2 px-7 py-3">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this transfer? This action cannot be undone.
          </p>
          <div className="mt-4 p-3 bg-gray-50 rounded border text-left">
            <p className="text-sm"><strong>Transfer ID:</strong> {transfer.transfer_id}</p>
            <p className="text-sm"><strong>Amount:</strong> {formatCurrency(transfer.amount)}</p>
            <p className="text-sm"><strong>Status:</strong> {transfer.status}</p>
            <p className="text-sm"><strong>Type:</strong> {transfer.type}</p>
          </div>
        </div>
        <div className="flex justify-center space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete Transfer
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default TransferManagement; 